/**
 * Playlist logic for the wdka-carousel element.
 *
 * Normalizes slide data from the two shapes it arrives in (inline PHP
 * bootstrap on front-page.php / single-slide.php, and the WP REST API
 * used for refreshes and central feeds), filters by schedule, and
 * handles fetching.
 */

const DAYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

/**
 * ACF datetimes are "YYYY-MM-DD HH:mm:ss" strings; make them parse
 * consistently as local time across browsers.
 */
function parseDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  const date = new Date(String(value).replace(" ", "T"));
  return isNaN(date.getTime()) ? null : date;
}

function asMedia(field) {
  if (!field || !field.url) return false;
  // Prefer the screen-sized derivative for images, to cut decode cost on the
  // weak Airtame hardware. The Airtame 2 outputs 1080×1920 (DPR 1), so
  // `screen_hd` matches the panel 1:1 — anything larger is decoded then
  // downscaled away. The 4K `screen` derivative is kept for a future
  // Airtame 3 (4K output); to switch, prefer it here. Falls back to the
  // original for videos (no sizes) and for images not yet regenerated.
  const sizes = field.sizes || {};
  const url = sizes.screen_hd || sizes.screen || field.url;
  return { url, alt: field.alt || "" };
}

/**
 * Cast a slide into the single shape the carousel renders from,
 * regardless of whether it came from the inline bootstrap or REST.
 */
export function normalizeSlide(raw) {
  const acf = raw.acf || raw;
  const type = acf.slide_type || raw.type || "regular";
  const isVideoType = type === "video";

  return {
    title:
      raw.title && typeof raw.title === "object"
        ? raw.title.rendered
        : raw.title || "",
    content:
      raw.content && typeof raw.content === "object"
        ? raw.content.rendered
        : raw.content || "",
    img: asMedia(acf.slide_image || raw.img),
    video: asMedia(acf.slide_video || raw.video),
    // Background video is software-decoded on the Airtame 2 and stalls badly
    // (~39 waiting events/play, ~27% dropped frames on the wh01 perf logs),
    // so it is disabled for regular slides. Non-regular (e.g. video) types may
    // still use it.
    backgroundVideo: type === "regular"
      ? false
      : asMedia(acf.background_video || raw.background_video),
    theme: isVideoType ? "jet" : acf.color_theme || raw.color_theme || "default",
    blendMode: acf.blend_mode || raw.blend_mode || "default",
    duration: parseInt(acf.duration || raw.duration, 10) || 10,
    startAt: parseDate(acf.start_at || raw.start_at),
    closeAt: parseDate(acf.close_at || raw.close_at),
    daysOfWeek: acf.days_of_week || raw.days_of_week || null,
    options: acf.options || raw.options || [],
    // ACF true_false comes back as 1/0 over REST and true/false inline.
    exclusive: Boolean(acf.exclusive ?? raw.exclusive),
    type
  };
}

/**
 * Takeover preemption: if any slide in the set is flagged exclusive,
 * show ONLY the exclusive ones. Otherwise the set is returned unchanged.
 * Because exclusivity is derived from the slides present at this moment
 * (and they carry their own start/close window), the screen reverts to
 * its normal playlist automatically once the exclusive slides lapse.
 */
export function applyExclusive(slides) {
  return slides.some(s => s.exclusive)
    ? slides.filter(s => s.exclusive)
    : slides;
}

/**
 * Schedule filter: start/close window plus day-of-week, matching the
 * behaviour of the old Vue carousel.
 */
export function isScheduledNow(slide, now = new Date()) {
  const { startAt, closeAt, daysOfWeek } = slide;

  if (startAt && now < startAt) return false;
  if (closeAt && now > closeAt) return false;

  // Slides without a day selection never played in the old carousel;
  // keep that behaviour so content does not suddenly appear.
  if (!daysOfWeek || !daysOfWeek.includes(DAYS[now.getDay()])) return false;

  return true;
}

function shuffle(list) {
  const result = list.slice();
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

async function fetchSlides(url) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText} for ${url}`);
  }
  const slides = await response.json();
  return slides.map(normalizeSlide).filter(s => isScheduledNow(s));
}

export class Playlist {
  /**
   * @param {Array} initialSlides raw slides from the inline bootstrap
   * @param {Array} centralFeeds [{name, limit}] from wp_localize_script
   */
  constructor(initialSlides = [], centralFeeds = []) {
    // Bootstrap slides are the screen's own, already schedule-filtered by
    // PHP; apply takeover preemption so an own-subsite event is live on
    // first paint. Channel exclusives join on the first refresh.
    this.slides = applyExclusive(initialSlides.map(normalizeSlide));
    this.index = 0;
    this.localEndpoint = "wp-json/wp/v2/slide/?order=desc";
    this.centralFeeds = centralFeeds.map(feed => ({
      url: `/${feed.name}/wp-json/wp/v2/slide/?order=desc`,
      limit: parseInt(feed.limit, 10) || 0
    }));
    this.nextSlides = null;
    this.refreshing = false;
  }

  get current() {
    return this.slides[this.index];
  }

  get length() {
    return this.slides.length;
  }

  /**
   * Move to the next slide. Kicks off a background refresh when nearing
   * the end of the list and swaps the refreshed list in at the wrap
   * point, mirroring the old carousel's update cycle.
   */
  advance() {
    this.index++;
    if (this.index >= this.slides.length - 1 || this.slides.length === 1) {
      this.refresh();
    }
    if (this.index >= this.slides.length) {
      if (this.nextSlides && this.nextSlides.length) {
        this.slides = this.nextSlides;
        this.nextSlides = null;
      }
      this.index = 0;
    }
    return this.current;
  }

  rewind() {
    this.index = this.index > 0 ? this.index - 1 : this.slides.length - 1;
    return this.current;
  }

  /**
   * The slide a normal advance() would land on next, without mutating
   * state. Used to preload the upcoming slide during the current dwell.
   * (At the wrap boundary a pending refresh may make advance() return a
   * different object; the carousel handles that by re-preparing.)
   */
  peekNext() {
    if (!this.slides.length) return undefined;
    const i = this.index + 1;
    return i < this.slides.length ? this.slides[i] : this.slides[0];
  }

  /** Fetch a fresh playlist (own slides + central feeds) in the background. */
  async refresh() {
    if (this.refreshing) return;
    this.refreshing = true;
    try {
      let slides = await fetchSlides(this.localEndpoint);

      for (const feed of this.centralFeeds) {
        try {
          const centralSlides = await fetchSlides(feed.url);
          slides = slides.concat(
            feed.limit ? shuffle(centralSlides).slice(0, feed.limit) : centralSlides
          );
        } catch (error) {
          // A broken central feed should never take the screen down.
          console.error("[wdka-carousel] central feed failed:", error);
        }
      }

      if (slides.length) {
        // Preempt across the full union (own + every subscribed channel),
        // so a takeover authored in any feed the screen sees engages here.
        this.nextSlides = applyExclusive(slides);
      }
    } catch (error) {
      console.error("[wdka-carousel] playlist refresh failed:", error);
    } finally {
      this.refreshing = false;
    }
  }
}
